import React, { useCallback, useEffect, useRef, useState } from 'react';
import DropDownButton from 'devextreme-react/drop-down-button';
import DateBox from 'devextreme-react/date-box';
import { Button } from 'devextreme-react/button';
import AntLocalization from '../utils/AntLocalization';
import AntTime from '../utils/AntTime';
import {
    formatDateFilter,
    isValidDateFilter,
    normalizeDateFilter,
    quickDateFilter,
    matchDateFilterPreset,
    DATE_FILTER_PRESETS
} from '../utils/AntGridDateFilterValue';

const PRESET_LABELS = {
    today: { vi: 'Hôm nay', en: 'Today', es: 'Hoy', id: 'Hari ini', ja: '今日', zh: '今天', ko: '오늘', hi: 'आज', ar: 'اليوم', fr: 'Ce jour' },
    yesterday: { vi: 'Hôm qua', en: 'Yesterday', fr: 'Hier', es: 'Ayer', id: 'Kemarin', ja: '昨日', zh: '昨天', ko: '어제', hi: 'कल', ar: 'أمس' },
    month: { vi: 'Tháng này', en: 'This month', fr: 'Ce mois-ci', es: 'este mes', id: 'Bulan ini', ja: '今月', zh: '本月', ko: '이번 달', hi: 'इस महीने', ar: 'هذا الشهر' }
};

export { formatDateFilter, isValidDateFilter, normalizeDateFilter };

const DROP_DOWN_OPTIONS = { width: 300 };

// Panel tự giữ ngày đang nhập; giá trị đã áp dụng đọc qua ref để hàm render của
// DropDownButton cố định (đổi template lúc popup đang đóng/mở làm popup đóng mất).
const DateFilterPanel = ({ valueRef, onApply, onClear }) => {
    const initial = valueRef.current;
    const [draftStart, setDraftStart] = useState(isValidDateFilter(initial) ? AntTime.fromUnix(initial.startDate).toDate() : null);
    const [draftEnd, setDraftEnd] = useState(isValidDateFilter(initial) ? AntTime.fromUnix(initial.endDate).toDate() : null);
    const value = valueRef.current;

    const hasDraft = Boolean(draftStart || draftEnd);
    const activePreset = matchDateFilterPreset(value);

    return (
        <div className="ant-grid-date-filter-panel">
            {/* Chọn nhanh: bấm là áp dụng ngay. */}
            <div className="ant-grid-date-filter-presets">
                {DATE_FILTER_PRESETS.map((preset) => (
                    <Button
                        key={preset}
                        text={AntLocalization.translateText(PRESET_LABELS[preset])}
                        stylingMode={activePreset === preset ? 'contained' : 'outlined'}
                        type={activePreset === preset ? 'default' : 'normal'}
                        onClick={() => {
                            const next = quickDateFilter(preset);
                            setDraftStart(AntTime.fromUnix(next.startDate).toDate());
                            setDraftEnd(AntTime.fromUnix(next.endDate).toDate());
                            onApply(next);
                        }}
                    />
                ))}
            </div>
            <div className="ant-grid-date-filter-field">
                <span className="ant-grid-date-filter-label">{AntLocalization.translateText({ vi: 'Từ ngày', en: 'From', fr: 'Du', es: 'Desde', id: 'Dari', ja: '開始日', zh: '开始日期', ko: '시작일', hi: 'से', ar: 'من' })}</span>
                <DateBox
                    type="date"
                    pickerType="calendar"
                    displayFormat="dd/MM/yyyy"
                    showClearButton={true}
                    value={draftStart}
                    onValueChanged={(e) => setDraftStart(e.value || null)}
                />
            </div>
            <div className="ant-grid-date-filter-field">
                <span className="ant-grid-date-filter-label">{AntLocalization.translateText({ vi: 'Đến ngày', en: 'To', fr: 'Au', es: 'Hasta', id: 'Sampai', ja: '終了日', zh: '结束日期', ko: '종료일', hi: 'तक', ar: 'إلى' })}</span>
                <DateBox
                    type="date"
                    pickerType="calendar"
                    displayFormat="dd/MM/yyyy"
                    showClearButton={true}
                    value={draftEnd}
                    onValueChanged={(e) => setDraftEnd(e.value || null)}
                />
            </div>
            {/* <div className="ant-grid-date-filter-hint">
                {AntLocalization.translateText({ vi: 'Chỉ nhập 1 ngày để lọc đúng ngày đó', en: 'Enter one date to filter a single day', fr: 'Saisissez une seule date pour filtrer ce jour', es: 'Ingrese una sola fecha para filtrar ese día', id: 'Isi satu tanggal untuk memfilter hari itu', ja: '1日だけ入力するとその日で絞り込みます', zh: '只填一个日期即可筛选当天', ko: '하루만 입력하면 해당 날짜만 필터링됩니다', hi: 'एक ही दिन फ़िल्टर करने हेतु एक तारीख भरें', ar: 'أدخل تاريخاً واحداً لتصفية ذلك اليوم' })}
            </div> */}
            <div className="ant-grid-date-filter-actions">
                <Button
                    text={AntLocalization.translateText({ vi: 'Xoá lọc', en: 'Clear', fr: 'Effacer', es: 'Limpiar', id: 'Hapus', ja: 'クリア', zh: '清除', ko: '지우기', hi: 'साफ़ करें', ar: 'مسح' })}
                    stylingMode="text"
                    disabled={!hasDraft && !isValidDateFilter(value)}
                    onClick={() => {
                        setDraftStart(null);
                        setDraftEnd(null);
                        onClear();
                    }}
                />
                <Button
                    text={AntLocalization.translateText({ vi: 'Áp dụng', en: 'Apply', fr: 'Appliquer', es: 'Aplicar', id: 'Terapkan', ja: '適用', zh: '应用', ko: '적용', hi: 'लागू करें', ar: 'تطبيق' })}
                    type="default"
                    stylingMode="contained"
                    disabled={!hasDraft}
                    onClick={() => onApply(normalizeDateFilter(draftStart, draftEnd))}
                />
            </div>
        </div>
    );
};

// Bộ lọc theo ngày trên thanh công cụ AntGrid: chưa lọc chỉ hiện icon lịch; đã lọc hiện
// "ngày" hoặc "từ ngày – đến ngày". Bấm vào mở panel để đổi hoặc xoá điều kiện.
export default function AntGridDateFilter(props) {
    const [value, setValue] = useState(isValidDateFilter(props.value) ? props.value : null);
    const dropDownRef = useRef();
    const valueRef = useRef(value);
    valueRef.current = value;

    useEffect(() => {
        setValue(isValidDateFilter(props.value) ? props.value : null);
    }, [props.value]);

    const commit = useCallback(
        (next) => {
            setValue(next);
            // Đóng bằng instance, KHÔNG điều khiển `opened` qua React state: devextreme-react
            // trả option về giá trị prop cũ, sau lần Áp dụng thì bấm mở lại bị đóng ngay.
            const instance = dropDownRef.current && dropDownRef.current.instance;
            if (instance) instance.close();
            if (props.onChange) props.onChange(next);
        },
        [props.onChange]
    );

    const commitRef = useRef(commit);
    commitRef.current = commit;

    const renderPanel = useCallback(
        () => <DateFilterPanel valueRef={valueRef} onApply={(next) => commitRef.current(next)} onClear={() => commitRef.current(null)} />,
        []
    );

    const label = formatDateFilter(value);

    return (
        <div className={'ant-grid-date-filter' + (label ? ' ant-grid-date-filter-active' : '')}>
            <DropDownButton
                icon="event"
                // Chuỗi rỗng (không phải undefined): devextreme-react bỏ qua prop undefined nên
                // xoá lọc xong nút vẫn giữ chữ ngày cũ.
                text={label || ''}
                hint={label || AntLocalization.translateText({ vi: 'Lọc theo ngày', en: 'Filter by date', fr: 'Filtrer par date', es: 'Filtrar por fecha', id: 'Filter tanggal', ja: '日付で絞り込み', zh: '按日期筛选', ko: '날짜별 필터', hi: 'तारीख से फ़िल्टर', ar: 'تصفية بالتاريخ' })}
                showArrowIcon={false}
                stylingMode="text"
                focusStateEnabled={false}
                disabled={props.disabled}
                ref={dropDownRef}
                dropDownOptions={DROP_DOWN_OPTIONS}
                dropDownContentRender={renderPanel}
            />
        </div>
    );
}
